import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Insight, InsightGenieContext, InsightService } from "@/services/api/InsightService";
import { LeadInfoField } from "@/types/leads/lead-info";

export const useInsights = (limit: number = 50, filters?: Record<string, any>, enabled: boolean = true) => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error } = useInfiniteQuery({
    queryKey: ["insights", limit, filters],
    queryFn: async ({ pageParam = 0 }) => {
      const result = await InsightService.all(filters, limit, pageParam);
      return result;
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce((sum, page) => sum + page.insights.length, 0);
      return totalFetched < (lastPage.count || 0) ? totalFetched : undefined;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    initialPageParam: 0,
    enabled,
  });

  const allInsights = data?.pages.flatMap((page) => page.insights) || [];

  return {
    data: allInsights,
    isLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    totalCount: data?.pages[0]?.count || 0,
  };
};

export const useAgentInsight = (agentId: string | undefined) => {
  return useQuery({
    queryKey: ["agent-insight", agentId],
    queryFn: () => (agentId ? InsightService.getByAgent(agentId) : null),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!agentId,
  });
};

export const useCreateInsight = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      name: string;
      description?: string;
      fields?: LeadInfoField[];
      is_default?: boolean;
    }) => {
      return InsightService.create(data.name, data.description, data.fields, data.is_default);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insights"] });
    },
  });
};

export const useGenerateInsightFields = () => {
  return useMutation({
    mutationFn: async ({ description, genieContext }: { description: string; genieContext?: InsightGenieContext }) => {
      return InsightService.generate(description, genieContext);
    },
  });
};

export const useCreateInsightFromDescription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ description, genieContext }: { description: string; genieContext?: InsightGenieContext }) => {
      return InsightService.createFromDescription(description, genieContext);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insights"] });
    },
  });
};

export const useUpdateInsight = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: number;
      updates: {
        name?: string;
        description?: string;
        fields?: LeadInfoField[];
        is_default?: boolean;
      };
    }) => {
      return InsightService.update(id, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insights"] });
      queryClient.invalidateQueries({ queryKey: ["agent-insight"] });
      queryClient.invalidateQueries({ queryKey: ["agent"] });
      queryClient.invalidateQueries({ queryKey: ["agents"] });
    },
  });
};

export const useDeleteInsight = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      return InsightService.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insights"] });
    },
  });
};

export const useDeleteUnassignedInsights = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (insightIds: number[]) => {
      await Promise.all(insightIds.map((id) => InsightService.delete(id)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insights"] });
    },
  });
};

export const useDuplicateInsight = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (insight: Insight) => {
      return InsightService.create(`${insight.name} (Copy)`, `Copy of ${insight.name}`, insight.fields || undefined, false);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insights"] });
    },
  });
};

export const useAttachInsight = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ agentId, insightId }: { agentId: string; insightId: number }) => {
      return InsightService.attachToAgent(agentId, insightId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent"] });
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: ["user-agents"] });
      queryClient.invalidateQueries({ queryKey: ["agents-list"] });
      queryClient.invalidateQueries({ queryKey: ["all-agents"] });
      queryClient.invalidateQueries({ queryKey: ["agent-insight"] });
      queryClient.invalidateQueries({ queryKey: ["insights"] });
    },
  });
};

export const useDetachInsight = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (agentId: string) => {
      return InsightService.detachFromAgent(agentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent"] });
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: ["user-agents"] });
      queryClient.invalidateQueries({ queryKey: ["agents-list"] });
      queryClient.invalidateQueries({ queryKey: ["all-agents"] });
      queryClient.invalidateQueries({ queryKey: ["agent-insight"] });
      queryClient.invalidateQueries({ queryKey: ["insights"] });
    },
  });
};

export const useCreateDefaultInsight = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (description?: string) => {
      return InsightService.createDefault("Default Lead Info", description || "Default fields for lead information");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insights"] });
    },
  });
};
